import React, { useState } from 'react';
import { GoalIntakeProps, GOAL_BALANCE_LABELS, GoalBalanceCategory } from './types';

/**
 * GoalIntake - Stap 1 van de intake flow
 * 
 * Gebruiker voert een nieuw doel in:
 * - Titel (verplicht, max 300 tekens)
 * - Beschrijving (optioneel)
 * - Start- en einddatum (verplicht voor tijdlijn)
 * - Levensbalans categorie (voor tijdstracking)
 * 
 * Status = draft na deze stap.
 */
const GoalIntake: React.FC<GoalIntakeProps> = ({
  onNext,
  initialTitle = '',
  initialDescription = '',
  initialStartDate = '',
  initialEndDate = '',
  initialBalanceCategory = 'work'
}) => {
  const [title, setTitle] = useState(initialTitle);
  const [description, setDescription] = useState(initialDescription);
  const [balanceCategory, setBalanceCategory] = useState<GoalBalanceCategory>(initialBalanceCategory);
  
  // Default dates: today and 1 year from now
  const today = new Date();
  const oneYearFromNow = new Date(today);
  oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
  
  const [startDate, setStartDate] = useState(initialStartDate || today.toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(initialEndDate || oneYearFromNow.toISOString().split('T')[0]);

  const canProceed = title.trim().length > 0 && startDate && endDate;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (canProceed) {
      onNext(title.trim(), description.trim() || undefined, startDate, endDate, balanceCategory);
    }
  };

  return (
    <div className="max-w-xl mx-auto">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">
            Wat is je doel?
          </h2>
          <p className="text-gray-600">
            Beschrijf wat je wilt bereiken. Wees eerlijk tegen jezelf.
          </p>
        </div>

        {/* Titel input */}
        <div>
          <label 
            htmlFor="goal-title" 
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Mijn doel
          </label>
          <input
            id="goal-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value.slice(0, 300))}
            placeholder="Bijvoorbeeld: Mijn eerste 10 klanten werven"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
            autoFocus
          />
          <div className="mt-1 text-right text-xs text-gray-400">
            {title.length}/300
          </div>
        </div>

        {/* Beschrijving textarea */}
        <div>
          <label 
            htmlFor="goal-description" 
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Beschrijving <span className="text-gray-400">(optioneel)</span>
          </label>
          <textarea
            id="goal-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Geef eventueel meer context over wat dit doel voor jou betekent..."
            rows={4}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors resize-none"
          />
        </div>

        {/* Tijdlijn grenzen */}
        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-sm font-medium text-gray-900 mb-1">
            Tijdlijn van je doel
          </h3>
          <p className="text-xs text-gray-500 mb-4">
            Wanneer ben je met dit doel begonnen (of wanneer ga je beginnen)? Je kunt ook een datum in het verleden kiezen als je al eerder bent gestart.
          </p>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label 
                htmlFor="start-date" 
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Startdatum <span className="text-xs font-normal text-gray-500">(ook verleden mogelijk)</span>
              </label>
              <input
                id="start-date"
                type="date"
                value={startDate}
                max={today.toISOString().split('T')[0]}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
              />
            </div>
            <div>
              <label 
                htmlFor="end-date" 
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Einddoel
              </label>
              <input
                id="end-date"
                type="date"
                value={endDate}
                min={startDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
              />
            </div>
          </div>
        </div>

        {/* Levensbalans categorie */}
        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-sm font-medium text-gray-900 mb-1">
            Levensbalans categorie
          </h3>
          <p className="text-xs text-gray-500 mb-4">
            In welke categorie past dit doel? Dit bepaalt waar de tijd wordt geteld in je levensbalans overzicht.
          </p>
          
          <div className="grid grid-cols-2 gap-3">
            {(Object.entries(GOAL_BALANCE_LABELS) as [GoalBalanceCategory, string][]).map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setBalanceCategory(value)}
                className={`py-3 px-4 rounded-lg border-2 text-left transition-all ${
                  balanceCategory === value
                    ? 'border-orange-500 bg-orange-50 text-orange-900'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="font-medium">{label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Submit button */}
        <div className="pt-4">
          <button
            type="submit"
            disabled={!canProceed}
            className={`w-full py-3 px-6 rounded-lg font-medium transition-all ${
              canProceed
                ? 'bg-orange-500 text-white hover:bg-orange-600 active:bg-orange-700'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            Volgende
          </button>
        </div>
      </form>
    </div>
  );
};

export default GoalIntake;
